import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/utils/api'
import { storage } from '@/utils/helpers'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(storage.get('app_token') || 'unauth')
  const user = ref(storage.get('user_data') || null)
  const isLoading = ref(false)
  const error = ref(null)
  
  const isLoggedIn = computed(() => token.value !== 'unauth')
  
  const initializeAuth = () => {
    const savedToken = storage.get('app_token')
    const savedUser = storage.get('user_data')
    if (savedToken && savedToken !== 'unauth') {
      token.value = savedToken
      user.value = savedUser
    }
  }
  
  const login = async (credentials) => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await authApi.login(credentials)
      const { token: newToken, user: responseUser } = response.data

      if (!newToken) {
        throw new Error('Login token was not returned by the server')
      }
      
      token.value = newToken
      storage.set('app_token', newToken)

      // The attendance API returns the token only from login. Load the
      // employee profile after setting the token so the dashboard has user data.
      const profileResponse = responseUser
        ? { data: responseUser }
        : await authApi.getProfile()
      user.value = profileResponse.data
      storage.set('user_data', profileResponse.data)
      
      router.push('/')
      return true
    } catch (err) {
      token.value = 'unauth'
      user.value = null
      storage.remove('app_token')
      storage.remove('user_data')
      error.value = err.response?.data?.message || 'Login gagal'
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  const logout = () => {
    token.value = 'unauth'
    user.value = null
    storage.remove('app_token')
    storage.remove('user_data')
    router.push('/login')
  }
  
  const updateUser = (userData) => {
    user.value = { ...user.value, ...userData }
    storage.set('user_data', user.value)
  }

  const fetchProfile = async () => {
    const response = await authApi.getProfile()
    user.value = response.data
    storage.set('user_data', response.data)
    return response.data
  }
  
  const updateProfileImage = (imageUrl) => {
    if (user.value) {
      user.value.image = imageUrl
      storage.set('user_data', user.value)
    }
  }
  
  return {
    token,
    user,
    isLoading,
    error,
    isLoggedIn,
    initializeAuth,
    login,
    logout,
    updateUser,
    fetchProfile,
    updateProfileImage
  }
})
